import type {
  DashboardSettingsResponseDto,
  MessageSequenceStatus,
  RebillMessageResponseDto,
} from '@api'
import { useUnit } from 'effector-react'
import { useMutation, useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast.tsx'
import { $creatorId } from '@/models/creators.ts'
import { rebillMessageService } from '@/modules/creator-settings/rebill-messages/service/rebill-message.ts'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { handleError } from '@/utils/error'

export function useToggleRebillMessageByMessageIdMutation() {
  const toast = useToast()
  const client = useQueryClient()
  const creatorId = useUnit($creatorId)
  const { settingsSortOptions } = useSettingsContext()

  return useMutation(
    ({
      messageId,
      value,
    }: {
      messageId: string
      value: MessageSequenceStatus
    }) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }

      return rebillMessageService.messageToggle(creatorId, messageId, value)
    },
    {
      onSuccess: (updatedMessageResponse) => {
        const updatedMessage =
          updatedMessageResponse as unknown as RebillMessageResponseDto

        client.setQueryData<DashboardSettingsResponseDto | undefined>(
          ['settings', creatorId, settingsSortOptions],
          (state) => {
            if (!state) return state
            toast.success('Settings updated')

            return {
              ...state,
              rebillMessages: state.rebillMessages.map((message) =>
                String(message.id) === String(updatedMessage.id)
                  ? updatedMessage
                  : message,
              ),
            }
          },
        )
      },
      onError: (error) => {
        handleError(error, 'Failed to toggle the message')
        client.invalidateQueries(['settings', creatorId, settingsSortOptions])
      },
    },
  )
}
